import React, { memo, useMemo, useEffect, useState, useRef } from 'react';
import LineChart from '../../charts/LineChart02';
import { tailwindConfig } from '../../utils/Utils';

// Memoized version of the LineChart component
const MemoizedLineChart = memo(LineChart);

function DashboardCard08({ chartData, chartView, onChartViewChange, isUsingCache }) {
  // Check if there are no data entries in the chartData
  const noDataAvailable = !chartData || chartData.labels.length === 0;
  
  const [isChartReady, setIsChartReady] = useState(false);
  const [currentView, setCurrentView] = useState(chartView);
  const isMountedRef = useRef(true);

  // Single effect to manage chart readiness
  useEffect(() => {
    // Always reset chart readiness when this effect runs
    setIsChartReady(false);
    
    // Update current view if it changed
    if (currentView !== chartView) {
      setCurrentView(chartView);
    }
    
    // Set chart ready after a short delay
    const timer = setTimeout(() => {
      setIsChartReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [chartView]); // Only depend on chartView

  // Memoize the chart options to avoid recalculating on every render
  const chartOptions = useMemo(() => ({
    layout: { 
      padding: 20 
    },
    scales: {
      x: {
        type: 'category', // Use category for both hourly and daily labels
        ticks: {
          autoSkip: false, // Don't skip dates
          maxRotation: chartView === 'today' ? 45 : 0, // Rotate hourly labels slightly
          color: '#F1C40F', // Retro yellow color
          font: {
            family: '"Share Tech Mono", monospace',
            weight: 'bold'
          },
          callback: function(value, index) {
            // For 7-day view, format the date labels nicely
            if (chartView === '7days') {
              const dateStr = this.getLabelForValue(value);
              const date = new Date(dateStr);
              if (isNaN(date.getTime())) {
                return dateStr; // Return as-is if invalid date
              }
              return date.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric' 
              });
            }
            // For today view, return the hour labels as-is
            return this.getLabelForValue(value);
          }
        },
        border: {
          display: false,
        },
        grid: {
          display: false, // Hide vertical grid lines
        },
      },
      y: {
        beginAtZero: true, // Ensure y-axis starts at 0
        min: 0, // Explicitly set y-axis minimum to 0
        border: {
          display: false,
        },
        ticks: {
          maxTicksLimit: 5, // Limit the number of ticks
          color: '#F1C40F', // Retro yellow color
          font: {
            family: '"Share Tech Mono", monospace',
            weight: 'bold'
          },
          callback: (value) => {
            if (value >= 1000) {
              return (value / 1000).toFixed(value % 1000 === 0 ? 0 : 1) + 'k';
            }
            return value.toString();
          },
        },
        grid: {
          color: 'rgba(241, 196, 15, 0.2)', // Subtle yellow grid lines
        },
      },
    },
    plugins: {
      legend: {
        display: false, // Hide the legend
      },
      tooltip: {
        backgroundColor: 'rgba(44, 62, 80, 0.95)',
        titleColor: '#F1C40F',
        bodyColor: '#ECF0F1',
        borderColor: '#F1C40F',
        borderWidth: 2,
        titleFont: {
          family: '"Share Tech Mono", monospace',
          weight: 'bold'
        },
        bodyFont: {
          family: '"Share Tech Mono", monospace'
        },
        callbacks: {
          title: function(context) {
            if (chartView === '7days') {
              // Format tooltip title for dates
              const dateStr = context[0].label;
              const date = new Date(dateStr);
              if (isNaN(date.getTime())) {
                return dateStr;
              }
              return date.toLocaleDateString('en-US', { 
                weekday: 'long',
                month: 'short', 
                day: 'numeric' 
              });
            }
            // For today view, show the hour
            return context[0].label;
          },
          label: (context) => `🎮 Plays: ${context.parsed.y.toLocaleString()}`, // Tooltip for numeric values with gaming icon
        },
      },
    },
    interaction: {
      intersect: false, // Tooltips appear near points
      mode: 'nearest', // Tooltips for the nearest point
    },
    elements: {
      line: {
        tension: 0.2, // Smooth curved lines
        borderJoinStyle: 'round', // Smooth corner transitions
      },
      point: {
        radius: 0, // Hide points unless needed
        hoverRadius: 6,
      },
    },
    maintainAspectRatio: false, // Disable maintaining the aspect ratio
    resizeDelay: 200, // Delay resizing
    responsive: true,
  }), [chartView]);

  // Memoize filled chart data to avoid unnecessary recalculations
  const filledChartData = useMemo(() => {
    if (!chartData || !chartData.datasets) {
      return { labels: [], datasets: [] };
    }
    
    return {
      ...chartData,
      datasets: chartData.datasets.map(dataset => ({
        ...dataset,
        tension: 0.2, // Set tension to 0.2 for smooth curved lines
        borderColor: '#F1C40F', // Retro yellow
        backgroundColor: 'rgba(241, 196, 15, 0.1)', // Subtle yellow fill
        pointHoverBackgroundColor: '#E74C3C', // Red highlight on hover
        pointHoverBorderColor: '#F1C40F',
        pointHoverBorderWidth: 3,
        borderWidth: 3,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 6,
        clip: 20,
      })),
    };
  }, [chartData]);

  if (noDataAvailable) {
    return (
      <div className="flex flex-col col-span-full sm:col-span-6 shadow-sm rounded-xl" style={{
        background: 'linear-gradient(135deg, #2C3E50 0%, #34495E 100%)',
        border: '2px solid rgba(241, 196, 15, 0.3)',
        fontFamily: '"Share Tech Mono", monospace'
      }}>
        <header className="px-5 py-4 flex items-center" style={{borderBottom: '2px solid rgba(241, 196, 15, 0.2)'}}>
          <h2 className="font-bold" style={{color: '#F1C40F'}}>📊 PERFORMANCE MONITOR</h2>
        </header>
        <div className="p-5 text-center" style={{color: '#95A5A6'}}>
          <div className="text-4xl mb-2">📊</div>
          <p className="font-bold">&gt;&gt;&gt; ACTIVATE CAMPAIGN TO MONITOR PERFORMANCE &lt;&lt;&lt;</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col col-span-full sm:col-span-6 shadow-sm rounded-xl" style={{
      background: 'linear-gradient(135deg, #2C3E50 0%, #34495E 100%)',
      border: '2px solid rgba(241, 196, 15, 0.3)',
      fontFamily: '"Share Tech Mono", monospace'
    }}>
      <header className="px-5 py-4 flex items-center justify-between" style={{borderBottom: '2px solid rgba(241, 196, 15, 0.2)'}}>
        <div className="flex items-center gap-2">
          <h2 className="font-bold" style={{color: '#F1C40F'}}>
            📈 {chartView === 'today' ? "TODAY'S ANALYTICS" : 'WEEKLY PERFORMANCE'}
          </h2>
        </div>
        
        {/* View Toggle */}
        <div className="flex rounded-lg p-1" style={{
          background: 'rgba(52, 73, 94, 0.6)',
          border: '1px solid rgba(241, 196, 15, 0.3)'
        }}>
          <button
            onClick={() => onChartViewChange('today')}
            className="px-3 py-1 text-sm font-bold rounded-md transition-all duration-300"
            style={chartView === 'today' ? {
              background: 'linear-gradient(135deg, #E74C3C 0%, #C0392B 100%)',
              border: '1px solid #F1C40F',
              color: '#F1C40F',
            } : {
              background: 'transparent',
              border: '1px solid transparent',
              color: '#95A5A6'
            }}
          >
            TODAY
          </button>
          <button
            onClick={() => onChartViewChange('7days')}
            className="px-3 py-1 text-sm font-bold rounded-md transition-all duration-300"
            style={chartView === '7days' ? {
              background: 'linear-gradient(135deg, #E74C3C 0%, #C0392B 100%)',
              border: '1px solid #F1C40F',
              color: '#F1C40F',
            } : {
              background: 'transparent',
              border: '1px solid transparent',
              color: '#95A5A6'
            }}
          >
            7 DAYS
          </button>
        </div>
      </header>
      {/* Render Memoized Line Chart */}
      <div className="relative h-[248px]" style={{background: 'linear-gradient(180deg, rgba(44, 62, 80, 0.3) 0%, rgba(52, 73, 94, 0.1) 100%)'}}>
        {(() => {
          return isChartReady ? (
            <>
              <MemoizedLineChart 
                key={`chart-${chartView}`} // Force remount on view change
                data={filledChartData} 
                options={chartOptions} 
                width={595} 
                height={248} 
              />
            </>
          ) : (
            <>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin w-8 h-8 border-4 border-yellow-400 border-t-transparent rounded-full mx-auto mb-2"></div>
                  <p className="text-sm font-bold" style={{color: '#F1C40F'}}>Loading Chart...</p>
                </div>
              </div>
            </>
          );
        })()}
      </div>
    </div>
  );
}

export default memo(DashboardCard08);